import type { JSX } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Chip, Spinner } from '@heroui/react';

import { useFrameworkDetail, useFrameworkManifest } from '~/hooks/useFrameworks';

import { FrameworkBreadcrumb } from './components/FrameworkBreadcrumb';
import { PatternContent } from './components/PatternContent';
import { formatFrameworkTitle, TEST_LAYER_LABELS } from './utils';

export function FrameworkPage(): JSX.Element {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const { error, framework, isLoading } = useFrameworkDetail(frameworkId);
  const { manifest } = useFrameworkManifest();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !framework) {
    return (
      <div className="py-20 text-center text-error">
        {error ? 'Failed to load framework.' : 'Framework not found.'}
      </div>
    );
  }

  // Get pattern_refs from manifest (detail endpoint doesn't include them)
  const manifestEntry = manifest?.frameworks.find(f => f.framework_id === frameworkId);
  const patternRefs = manifestEntry?.pattern_refs ?? [];

  return (
    <div>
      <FrameworkBreadcrumb frameworkId={framework.framework_id} testLayer={framework.test_layer} />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-3">
          {formatFrameworkTitle(framework.framework_id)}
        </h1>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Chip color="secondary" variant="flat">
            {TEST_LAYER_LABELS[framework.test_layer] ?? framework.test_layer}
          </Chip>
          <Chip variant="bordered">{framework.framework_type}</Chip>
        </div>
        {framework.intent && <p className="text-muted">{framework.intent}</p>}
      </div>

      {/* Apply When / Do Not Apply */}
      {(framework.apply_when.length > 0 || framework.do_not_apply.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {framework.apply_when.length > 0 && (
            <div className="p-4 rounded-lg border border-default bg-surface">
              <h2 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                <CheckCircleIcon className="h-4 w-4 text-success" />
                Apply When
              </h2>
              <ul className="space-y-1.5">
                {framework.apply_when.map(item => (
                  <li key={item} className="text-sm text-muted flex items-start gap-2">
                    <span className="text-success mt-0.5 shrink-0">+</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {framework.do_not_apply.length > 0 && (
            <div className="p-4 rounded-lg border border-default bg-surface">
              <h2 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                <XCircleIcon className="h-4 w-4 text-error" />
                Do Not Apply
              </h2>
              <ul className="space-y-1.5">
                {framework.do_not_apply.map(item => (
                  <li key={item} className="text-sm text-muted flex items-start gap-2">
                    <span className="text-error mt-0.5 shrink-0">-</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Markdown content */}
      <PatternContent content={framework.content} />

      {/* Related patterns */}
      {patternRefs.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-foreground mb-3">Related Patterns</h2>
          <div className="flex flex-wrap gap-2">
            {patternRefs.map(patternId => (
              <NavLink key={patternId} to={`/patterns/${patternId}`}>
                <Chip
                  size="sm"
                  variant="bordered"
                  className="cursor-pointer hover:bg-surface-elevated"
                >
                  {patternId}
                </Chip>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
